import { useEffect, useState } from "react";
import { usePost } from "./API";
import { useToast } from "@aidkitorg/component-library";
import { TagDisplay } from "./Components/TagDisplay";
import { TagSelector } from "./Components/TagSelector";
import { Button } from "@aidkitorg/component-library";
import { useLocalizedStrings } from "./Localization";

// Type guard function to safely access metadata object
function isMetadataObject(obj: any): obj is { tags?: string[] } {
  return obj && typeof obj === 'object' && !Array.isArray(obj);
}

interface SupportCaseTagsProps {
  caseId: string;
  metadata: any;
  onMetadataChange?: (metadata: any) => void;
}

export function SupportCaseTags({ caseId, metadata, onMetadataChange }: SupportCaseTagsProps) {
  const [tags, setTags] = useState<string[]>([]);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const { toast } = useToast();
  const setCaseTags = usePost("/support/set_case_tags");

  const L = useLocalizedStrings();

  // Initialize tags from server data
  useEffect(() => {
    if (metadata && isMetadataObject(metadata)) {
      setTags(metadata.tags ?? []);
    }
  }, [metadata]);

  const handleTagsChange = async (newTags: string[]) => {
    // Optimistically update UI
    const previousTags = tags;
    setTags(newTags);
    
    try {
      await setCaseTags({
        caseId,
        tags: newTags
      });

      // Update parent component if callback provided
      if (onMetadataChange) {
        onMetadataChange({
          ...metadata,
          tags: newTags
        });
      }
    } catch (error) {
      // Revert on failure
      setTags(previousTags);
      toast({
        description: L.support.tags.problem_updating_tags,
        variant: 'error'
      });
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3">
        <TagDisplay
          tags={tags}
          onRemove={(tag) => {
            const newTags = tags.filter(t => t !== tag);
            handleTagsChange(newTags);
          }}
          onAddTagClick={() => {
            setIsAddingTag(true);
          }}
          addingNewTag={isAddingTag}
          className=""
        />
      </div>
      {isAddingTag && (
        <div className="flex items-center gap-2">
          <TagSelector
            onChange={handleTagsChange}
            selectedTags={tags}
            className="w-64"
            placeholder={L.support.tags.search_tags}
          />
          <Button 
            variant="secondary" 
            className="text-sm" 
            onClick={() => setIsAddingTag(false)}
          >
            {L.support.tags.done}
          </Button>
        </div>
      )}
    </div>
  );
}
